import * as React from "react";
import { ExecutionRulesSimulatorPanel } from "../ExecutionRulesSimulator";
import type { PolicyTreeV2 } from "../../../types/tradePolicy";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import type { WorkspaceCreateContext, WorkspaceNode } from "../../../types/workspace";
import { WorkspaceWizardDialog, WORKSPACE_SIMULATOR_WIZARD_DIALOG_SIZE_CLASS } from "./WorkspaceWizardDialog";

export type WorkspaceSimulatorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: WorkspaceAccountScope;
  /** When set, generate attaches the simulation onto the canvas under `parentNodeId`. */
  workspaceContext?: WorkspaceCreateContext;
  /** Unused by the panel today; kept for canvas call sites that pass the parent node. */
  parentNode?: WorkspaceNode;
  scopedReportId?: string | null;
  lockBrokerAccountCode: string;
  onSimulationSaved: (backtestId: string) => void;
  /** Canvas: job queued — insert processing node (host may close dialog and wait/persist). */
  onSimulationQueued?: (preview: {
    title: string;
    jobId?: string;
    /** Submitted simulate policy — used to decide derived CP lineage forks. */
    policy?: unknown;
    /** Post-hydrate rebuild baseline (same pipeline as Generate). */
    baselinePolicyForLineage?: unknown;
    /** Simulator: submitted rebuild differs from post-hydrate baseline. */
    policyChangedFromBaseline?: boolean;
  }) => void;
  /** Canvas: dismiss processing node when queue/save fails. */
  onSimulationFailed?: () => void;
  /**
   * Location state merged into the standalone results navigation after Generate
   * (e.g. `{ fromWorkspace: true }` from the hub).
   */
  resultsLocationState?: Record<string, unknown>;
  /** Prefill classic / bundle policy editor (omit `startWithEmptyPolicy`). */
  prefillPolicyTree?: unknown;
  /** Prefill v2 trade-management policy. */
  prefillPolicy?: PolicyTreeV2 | null;
  policyMode?: "v1" | "v2";
  prefillStrategyId?: string | null;
  /** Lock strategy when running from a canvas policy. */
  strategyLocked?: boolean;
  prefillSymbol?: string | null;
  prefillWindow?: {
    start_date?: string | null;
    end_date?: string | null;
    timeframe?: string | null;
  } | null;
  /** Prefill / lock candle TF (policy Run). */
  defaultCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  candleTimeframeLocked?: boolean;
  /** Dual-TF RA+EP. */
  entryCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  managementCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  /**
   * Canvas policy Run: choose adopted report window (`report_id`) or any free
   * date range (omit `report_id`). Requires `scopedReportId`.
   */
  allowFreeWindowOutsideReport?: boolean;
  /** Stepped wizard start step (0 scope, 1 policy, 2 review). */
  initialSimulatorWizardStep?: number;
  /**
   * When set, Generate shows this warning and does not start (e.g. report sim cap).
   */
  workspaceSimulationBlockedReason?: string | null;
  /** Dialog title when running from an existing policy (default: New simulation). */
  title?: string;
  description?: string;
};

/**
 * Create-simulation host — centered dialog with the stepped simulator wizard.
 * Matches strategy / report create dialog chrome (stepper pills + pinned footer).
 */
export function WorkspaceSimulatorDialog({
  open,
  onOpenChange,
  scope: _scope,
  workspaceContext,
  parentNode: _parentNode,
  scopedReportId,
  lockBrokerAccountCode,
  onSimulationSaved,
  onSimulationQueued,
  onSimulationFailed,
  resultsLocationState,
  prefillPolicyTree,
  prefillPolicy,
  policyMode = "v1",
  prefillStrategyId,
  strategyLocked = false,
  prefillSymbol,
  prefillWindow,
  defaultCandleTimeframe,
  candleTimeframeLocked,
  entryCandleTimeframe,
  managementCandleTimeframe,
  allowFreeWindowOutsideReport,
  initialSimulatorWizardStep,
  workspaceSimulationBlockedReason,
  title,
  description: descriptionOverride,
}: WorkspaceSimulatorDialogProps): React.ReactElement {
  const canvasAttach = Boolean(workspaceContext?.workspaceId?.trim());
  const hasPolicyPrefill = prefillPolicyTree != null || prefillPolicy != null;
  const [panelEpoch, setPanelEpoch] = React.useState(0);

  React.useEffect(() => {
    if (!open) return;
    // Fresh panel each open so wizard step / generate arming cannot leak.
    setPanelEpoch((n) => n + 1);
  }, [open]);

  const handleBacktestSaved = React.useCallback(
    (backtestId: string) => {
      onSimulationSaved(backtestId);
      onOpenChange(false);
    },
    [onOpenChange, onSimulationSaved]
  );

  const handleWizardExit = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const description =
    descriptionOverride ??
    (hasPolicyPrefill
      ? "Review the loaded policy, adjust the window if needed, then generate. A derived policy is created only if you change the rules."
      : scopedReportId
        ? canvasAttach
          ? "Run a simulation linked to this report and workspace."
          : "Run a simulation linked to this report for the active account."
        : canvasAttach
          ? "Run a standalone simulation linked to this workspace."
          : "Walk through scope, build your policy, then generate a simulation for the active account.");

  return (
    <WorkspaceWizardDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title ?? (hasPolicyPrefill ? "Run simulation" : "New simulation")}
      description={description}
      animateHeight
      pinFooterSlot
      className={WORKSPACE_SIMULATOR_WIZARD_DIALOG_SIZE_CLASS}
      bodyClassName="flex flex-col p-0"
    >
      {open ? (
        <ExecutionRulesSimulatorPanel
          key={`sim-wizard-${panelEpoch}`}
        variant="standalone"
        scopedReportId={scopedReportId ?? null}
        layout="embedded"
        simulatorWizardLayout
        startWithEmptyPolicy={!hasPolicyPrefill}
        prefillPolicyTree={prefillPolicyTree}
        prefillPolicy={prefillPolicy}
        policyMode={policyMode}
        prefillStrategyId={prefillStrategyId ?? null}
        strategyLocked={strategyLocked}
        prefillSymbol={prefillSymbol ?? null}
        prefillWindow={prefillWindow ?? null}
        defaultCandleTimeframe={defaultCandleTimeframe}
        candleTimeframeLocked={candleTimeframeLocked}
        entryCandleTimeframe={entryCandleTimeframe}
        managementCandleTimeframe={managementCandleTimeframe}
        allowFreeWindowOutsideReport={Boolean(
          allowFreeWindowOutsideReport ?? (hasPolicyPrefill && scopedReportId)
        )}
        initialSimulatorWizardStep={initialSimulatorWizardStep ?? 0}
        lockedBrokerAccountCode={lockBrokerAccountCode}
        hideSavedBacktestsButton
        workspaceSimulationContext={workspaceContext}
        workspaceSimulationBlockedReason={workspaceSimulationBlockedReason}
        onBacktestSaved={handleBacktestSaved}
        onWorkspaceSimulationQueued={onSimulationQueued}
        onWorkspaceSimulationFailed={onSimulationFailed}
        onWizardExit={handleWizardExit}
          resultsLocationState={resultsLocationState}
        />
      ) : null}
    </WorkspaceWizardDialog>
  );
}
